import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "../Master/Ledger/ShowLedger.css";
import { showToast } from "../../utils/customToast";
import { SECURITY_KEY } from "../../globals";
import { useCompany } from "../../context/CompanyContext";
import { apiWithLog } from "../../utils/apiWithLog";
import DeleteConfirmModal from "../../Component/DeleteModal/DeleteConfirmModal";
import { fetchUserCompanyLimit } from "../../utils/companyLimitHelper";
import { MasterTableSkeleton } from "../../Component/Skeleton";

const Selectcompny = () => {
  const [responseData, setResponseData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteItemName, setDeleteItemName] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [states, setStates] = useState([]);
  const [maxCompanies, setMaxCompanies] = useState(null);

  const hiddenColumns = ["StateCode", "MasterId", "CompID"];

  const { setSelectedCompany } = useCompany();

  const BASE_REST_API_URL = process.env.REACT_APP_BASE_REST_API_URL;

  /* ===================== AUTO SELECT FIRST COMPANY ===================== */
  const autoSelectFirstCompany = (companyList) => {
    if (companyList.length > 0) {
      const firstCompany = companyList[0];

      const selectedCompany = {
        id: firstCompany.CompID,
        name: firstCompany.Name,
        CompanyCode: firstCompany.CompanyCode || "",
        CompanyColourCode: firstCompany.CompanyColourCode || "",
      };

      setSelectedCompany(selectedCompany);
      localStorage.setItem(
        "selectedCompany",
        JSON.stringify(selectedCompany)
      );
    } else {
      setSelectedCompany(null);
      localStorage.removeItem("selectedCompany");
    }
  };
  /* ===================================================================== */

  /* ===================== FETCH DATA ===================== */
  useEffect(() => {
    fetchData();
    fetchStates();
    loadCompanyLimit();
  }, []);

  const loadCompanyLimit = async () => {
    try {
      const limitData = await fetchUserCompanyLimit();
      if (limitData && limitData.maxCompanies !== null && limitData.maxCompanies !== undefined) {
        setMaxCompanies(limitData.maxCompanies);
      }
    } catch (err) {
      console.warn("Could not load user company limit:", err);
    }
  };

  useEffect(() => {
    applyFilter(searchQuery);
  }, [responseData, searchQuery]);

  const fetchData = async () => {
    setError(null);
    setLoading(true);

    const loggedInUserId = localStorage.getItem("userId");

    const data = new URLSearchParams();
    data.append("SecurityKey", SECURITY_KEY);
    data.append("TableName", "company");
    data.append("WhereCondition", `MasterId=${loggedInUserId}`);
    data.append("*", "*");

    try {
      const apiUrl = `${BASE_REST_API_URL}/SelectData.aspx`;

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });

      const jsonData = await response.json();

      if (jsonData.Response) {
        setResponseData(jsonData.Response);
        setFilteredData(jsonData.Response);

        // ✅ auto select first company on initial load
        autoSelectFirstCompany(jsonData.Response);
      } else {
        setError("No data found.");
      }
    } catch (error) {
      setError("Failed to fetch data.");
    } finally {
      setLoading(false);
    }
  };

  const fetchStates = async () => {
    const data = new URLSearchParams();
    data.append("SecurityKey", SECURITY_KEY);
    data.append("TableName", "gststates");
    data.append("WhereCondition", "All");
    data.append("*", "*");

    try {
      const apiUrl = `${BASE_REST_API_URL}/SelectData.aspx`;
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });

      const json = await response.json();
      if (json.Response) setStates(json.Response);
    } catch (error) {
      console.error(error);
    }
  };

  const getStateName = (stateId) => {
    const match = states.find(
      (state) => String(state.ID) === String(stateId)
    );
    return match ? match.State : "N/A";
  };

  const applyFilter = (query) => {
    if (!query) {
      setFilteredData(responseData);
    } else {
      const lowerQuery = query.toLowerCase();
      setFilteredData(
        responseData.filter((item) =>
          Object.values(item).some(
            (val) =>
              val && val.toString().toLowerCase().includes(lowerQuery)
          )
        )
      );
    }
    setCurrentPage(1);
  };

  const confirmDelete = (item) => {
    setDeleteId(item.CompID);
    setDeleteItemName(item.Name || `Company #${item.CompID}`);
    setDeleteModalOpen(true);
  };

  /* ===================== DELETE COMPANY ===================== */
  const handleDelete = async () => {
    if (loading) return;
    if (!deleteId) return;

    // ===== Prepare Form Data =====
    const deleteData = new URLSearchParams();
    deleteData.append("SecurityKey", SECURITY_KEY);
    deleteData.append("TableName", "company");
    deleteData.append("WhereCondition", `CompID=${deleteId}`);

    const deleteUrl = `${BASE_REST_API_URL}/deletedata.aspx`;

    try {
      setLoading(true);

      // ===== API Call with Logging =====
      const res = await apiWithLog({
        method: "POST",
        url: deleteUrl,
        data: deleteData.toString(),
        module: "Company",
        action: "D",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });

      // ===== Handle ASP.NET Response =====
      let result = res?.data || res;

      if (typeof result === "string") {
        if (
          result.toLowerCase().includes("deleted") ||
          result.toLowerCase().includes("success") ||
          result.toLowerCase().includes("ok")
        ) {
          showToast.delete("Company deleted successfully!");

          setResponseData((prev) => {
            const newList = prev.filter(
              (item) => item.CompID !== deleteId
            );

            // Auto select first company
            autoSelectFirstCompany(newList);
            return newList;
          });

          setFilteredData((prev) =>
            prev.filter((item) => item.CompID !== deleteId)
          );
          setDeleteModalOpen(false);
        } else {
          showToast.error("Delete failed.");
        }
      } else {
        // JSON case
        showToast.delete("Company deleted successfully!");

        setResponseData((prev) => {
          const newList = prev.filter(
            (item) => item.CompID !== deleteId
          );

          autoSelectFirstCompany(newList);
          return newList;
        });

        setFilteredData((prev) =>
          prev.filter((item) => item.CompID !== deleteId)
        );
        setDeleteModalOpen(false);
      }
    } catch (error) {
      console.error("Delete error:", error);
      showToast.error("Failed to delete company.");
    } finally {
      setDeleteId(null);
      setDeleteItemName("");
      setLoading(false);
    }
  };

  /* ========================================================= */

  /* ===================== PAGINATION ===================== */
  const lastIndex = currentPage * recordsPerPage;
  const firstIndex = lastIndex - recordsPerPage;
  const currentRecords = filteredData.slice(firstIndex, lastIndex);
  const totalPages = Math.ceil(filteredData.length / recordsPerPage);

  const getLogoUrl = (fileName) => {
    if (!fileName || fileName === "null") return null;
    const asString = fileName.toString().trim();
    if (/^https?:\/\//i.test(asString)) return asString;

    let cleanedPath = asString.replace(/^~[\\/]+/, "");
    cleanedPath = cleanedPath.replace(/\\/g, "/");
    cleanedPath = cleanedPath.replace(/^\/+/, "");

    const restApiRoot = BASE_REST_API_URL.replace(/\/+$/, "");
    const uploadRoot = restApiRoot.replace(/\/restapi$/i, "");

    if (/^uploads\//i.test(cleanedPath)) {
      return `${uploadRoot}/${cleanedPath}`;
    }

    return `${restApiRoot}/${cleanedPath}`;
  };

  const userType = localStorage.getItem("userType");
  const isSuperAdmin = String(userType) === "9";
  const isLimitReached = !isSuperAdmin && maxCompanies !== null && responseData.length >= maxCompanies;

  return (
    <div className="ledger_page_container">
      {/* TOP HEADER & BREADCRUMBS */}
      <div className="ledger_header_bar">
        <div className="ledger_header_title_group">
          <h4 className="ledger_header_title">Company Master</h4>
          <div className="ledger_breadcrumbs">
            <Link to="/home">Home</Link>
            <span>/</span>
            <span>Master</span>
            <span>/</span>
            <span>Company</span>
          </div>
        </div>
      </div>

      {/* TOP CONTROLS CARD */}
      <div className="ledger_top_controls">
        <div className="ledger_top_left">
          {isLimitReached ? (
            <button
              type="button"
              className="new_ledger_btn"
              style={{
                backgroundColor: "#a0aec0",
                cursor: "not-allowed",
                boxShadow: "none",
              }}
              onClick={() => {
                showToast.error(
                  `You have reached the maximum company limit of ${maxCompanies} companies allowed.`
                );
              }}
              title={`Company limit reached (${responseData.length}/${maxCompanies})`}
            >
              <i className="fa-solid fa-lock"></i> + Add Company
            </button>
          ) : (
            <Link to="/compney" className="new_ledger_btn">
              + Add Company
            </Link>
          )}

          {maxCompanies !== null && (
            <span
              className="ledger_company_badge"
              style={{
                backgroundColor: isLimitReached ? "#fee2e2" : "#e0f2fe",
                color: isLimitReached ? "#b91c1c" : "#0369a1",
                border: `1px solid ${isLimitReached ? "#fca5a5" : "#bae6fd"}`,
              }}
              title={`Maximum companies allowed according to MasterData: ${maxCompanies}`}
            >
              <i
                className={
                  isLimitReached
                    ? "fa-solid fa-triangle-exclamation"
                    : "fa-solid fa-building"
                }
              ></i>
              {responseData.length} / {maxCompanies} Companies
              {isLimitReached && " (Limit Reached)"}
            </span>
          )}
        </div>

        <div className="ledger_top_right">
          <div className="ledger_search_box">
            <input
              type="search"
              className="ledger_search_input"
              placeholder="Search companies..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="ledger_records_select_group">
            <label htmlFor="recordsPerPage">Show:</label>
            <select
              id="recordsPerPage"
              className="ledger_records_select"
              value={recordsPerPage}
              onChange={(e) => {
                setRecordsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={15}>15</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>
      </div>

      {/* TABLE CARD */}
      <div className="ledger_table_section">
        {loading && (
          <MasterTableSkeleton columns={7} rows={6} hasToolbar={false} />
        )}

        {error && !loading && (
          <div className="ledger_empty_state">
            <h6>Error Loading Data</h6>
            <p>{error}</p>
          </div>
        )}

        {!loading && !error && filteredData.length > 0 && (
          <>
            <div className="ledger_table_wrapper">
              <table className="ledger_table">
                <thead>
                  <tr>
                    {Object.keys(filteredData[0])
                      .filter((key) => !hiddenColumns.includes(key))
                      .map((key) => (
                        <th key={key}>
                          {key === "TelNo"
                            ? "Tel No"
                            : key === "StateID"
                            ? "State"
                            : key === "GSTNO"
                            ? "GST No"
                            : key === "LogoPath"
                            ? "Logo"
                            : key === "PanNo"
                            ? "PAN No"
                            : key === "BankAccountNo"
                            ? "Bank Account No"
                            : key === "IFSCCode"
                            ? "IFSC Code"
                            : key === "BankName"
                            ? "Bank Name"
                            : key === "CompanyCode"
                            ? "Company Code"
                            : key === "CompanyColourCode"
                            ? "Company Color"
                            : key.charAt(0).toUpperCase() + key.slice(1)}
                        </th>
                      ))}
                    <th>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {currentRecords.map((item, index) => (
                    <tr key={index}>
                      {Object.entries(item)
                        .filter(([key]) => !hiddenColumns.includes(key))
                        .map(([key, val], i) => (
                          <td
                            key={i}
                            className={key === "Name" ? "ledger_name_cell" : ""}
                          >
                            {key === "LogoPath" ? (
                              getLogoUrl(item.LogoPath) ? (
                                <img
                                  src={getLogoUrl(item.LogoPath)}
                                  alt="Logo"
                                  style={{
                                    width: "42px",
                                    height: "42px",
                                    objectFit: "contain",
                                    borderRadius: "4px",
                                    border: "1px solid #e2e8f0",
                                    background: "#fff",
                                    padding: "2px",
                                  }}
                                />
                              ) : (
                                <span style={{ color: "#94a3b8", fontSize: "11px" }}>No Logo</span>
                              )
                            ) : key === "Name" ? (
                              <span
                                style={{
                                  fontWeight: "600",
                                  color: item.CompanyColourCode || "#000000",
                                }}
                              >
                                {val || "-"}
                              </span>
                            ) : key === "CompanyCode" ? (
                              <span style={{ fontWeight: "600", letterSpacing: "0.5px" }}>
                                {val || "-"}
                              </span>
                            ) : key === "CompanyColourCode" ? (
                              val && val !== "-" && val !== "null" ? (
                                <div style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                                  <span
                                    style={{
                                      display: "inline-block",
                                      width: "18px",
                                      height: "18px",
                                      borderRadius: "4px",
                                      backgroundColor: val,
                                      border: "1px solid #cbd5e1",
                                    }}
                                  ></span>
                                  <span style={{ fontSize: "12px", fontFamily: "monospace" }}>
                                    {val}
                                  </span>
                                </div>
                              ) : (
                                <span style={{ color: "#94a3b8" }}>-</span>
                              )
                            ) : key === "StateID" ? (
                              getStateName(val)
                            ) : (
                              val?.toString() || "-"
                            )}
                          </td>
                        ))}

                      <td className="ledger_actions_cell">
                        <Link
                          to="/compupdatedata"
                          state={{ responseData: item }}
                        >
                          <button
                            type="button"
                            className="ledger_btn_edit"
                            title="Edit Company"
                          >
                            <i className="fa-solid fa-pen-to-square"></i>
                          </button>
                        </Link>

                        <button
                          type="button"
                          className="ledger_btn_delete"
                          onClick={() => confirmDelete(item)}
                          title="Delete Company"
                        >
                          <i className="fa-solid fa-trash-can"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* PAGINATION SECTION */}
            <div className="ledger_pagination_container">
              <div className="ledger_pagination_info">
                Showing {firstIndex + 1} to{" "}
                {Math.min(lastIndex, filteredData.length)} of{" "}
                {filteredData.length} records
              </div>

              <div className="ledger_pagination_controls">
                <button
                  type="button"
                  className="ledger_page_btn"
                  onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                <span className="ledger_page_badge">
                  Page {currentPage} of {totalPages || 1}
                </span>
                <button
                  type="button"
                  className="ledger_page_btn"
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages || totalPages === 0}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}

        {!loading && !error && filteredData.length === 0 && (
          <div className="ledger_empty_state">
            <h6>No Companies Found</h6>
            <p>
              {searchQuery
                ? `No companies matching "${searchQuery}"`
                : "No companies available."}
            </p>
          </div>
        )}
      </div>

      {/* Reusable Custom Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDelete}
        title="Delete Company?"
        message="Are you sure you want to delete this company? All associated records may be impacted."
        itemName={deleteItemName}
        loading={loading}
      />
    </div>
  );
};

export default Selectcompny;








// import React, { useState, useEffect } from "react";
// import { Link } from "react-router-dom";
// import "../Master/selectdata.css";
// import { SECURITY_KEY } from "../../globals";

// const Selectcompny = () => {
//   const [responseData, setResponseData] = useState([]);
//   const [filteredData, setFilteredData] = useState([]);
//   const [error, setError] = useState(null);
//   const [currentPage, setCurrentPage] = useState(1);
//   const [deleteId, setDeleteId] = useState(null);
//   const [searchQuery, setSearchQuery] = useState("");
//   const [loading, setLoading] = useState(false);
//   const [recordsPerPage, setRecordsPerPage] = useState(10);
//   const [states, setStates] = useState([]);

//   const hiddenColumns = ["StateCode", "MasterId", "CompID"];

//   // Fetch company data and states once on mount
//   useEffect(() => {
//     fetchData();
//     fetchStates();
//   }, []);

//   // Re-filter data when searchQuery or responseData changes
//   useEffect(() => {
//     applyFilter(searchQuery);
//   }, [responseData, searchQuery]);

//   // Fetch company data
//   const fetchData = async () => {
//     setError(null);
//     setLoading(true);

//     const loggedInUserId = localStorage.getItem("userId");

//     const data = new URLSearchParams();
//     data.append("SecurityKey", SECURITY_KEY);
//     data.append("TableName", "company");
//     data.append("WhereCondition", `MasterId=${loggedInUserId}`);
//     data.append("*", "*");

//     try {
//       const apiUrl = "https://etour.responseinfoway.com/restapi/Selectdata.aspx";

//       const response = await fetch(apiUrl, {
//         method: "POST",
//         headers: { "Content-Type": "application/x-www-form-urlencoded" },
//         body: data.toString(),
//       });

//       if (!response.ok)
//         throw new Error(`HTTP Error! Status: ${response.status}`);

//       const jsonData = await response.json();

//       if (jsonData.Response) {
//         setResponseData(jsonData.Response);
//         setFilteredData(jsonData.Response);
//         console.log("📦 Response Data:", jsonData.Response);
//       } else {
//         setError("No data found.");
//       }
//     } catch (error) {
//       console.error("Error fetching data:", error);
//       setError("Failed to fetch data. Please reference this page.");
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Fetch states data
//   const fetchStates = async () => {
//     const data = new URLSearchParams();
//     data.append("SecurityKey", SECURITY_KEY);
//     data.append("TableName", "gststates");
//     data.append("WhereCondition", "All");
//     data.append("*", "*");

//     try {
//       const apiUrl = "https://etour.responseinfoway.com/restapi/Selectdata.aspx";

//       const response = await fetch(apiUrl, {
//         method: "POST",
//         headers: { "Content-Type": "application/x-www-form-urlencoded" },
//         body: data.toString(),
//       });

//       if (!response.ok)
//         throw new Error(`HTTP Error! Status: ${response.status}`);

//       const json = await response.json();

//       if (json.Response) {
//         setStates(json.Response);
//       }
//     } catch (error) {
//       console.error("Failed to fetch states:", error);
//     }
//   };

//   //   Get state name by ID from fetched states
//   const getStateName = (stateId) => {
//     const match = states.find((state) => String(state.ID) === String(stateId));
//     return match ? match.State : "N/A";
//   };

//   // Apply search filter on company data
//   const applyFilter = (query) => {
//     if (!query) {
//       setFilteredData(responseData);
//     } else {
//       const lowerQuery = query.toLowerCase();
//       const filtered = responseData.filter((item) =>
//         Object.values(item).some(
//           (val) => val && val.toString().toLowerCase().includes(lowerQuery)
//         )
//       );
//       setFilteredData(filtered);
//     }
//     setCurrentPage(1);
//   };

//   // Set delete ID for confirmation modal
//   const confirmDelete = (compId) => setDeleteId(compId);

//   // Handle delete request
//   const handleDelete = async () => {
//     if (!deleteId) return;

//     const deleteData = new URLSearchParams();
//     deleteData.append("SecurityKey", SECURITY_KEY);
//     deleteData.append("TableName", "company");
//     deleteData.append("WhereCondition", `CompID=${deleteId}`);

//     try {
//       const response = await fetch(
//         "https://etour.responseinfoway.com/restapi/deletedata.aspx",
//         {
//           method: "POST",
//           headers: { "Content-Type": "application/x-www-form-urlencoded" },
//           body: deleteData.toString(),
//         }
//       );

//       if (!response.ok)
//         throw new Error(`API error! HTTP Status: ${response.status}`);

//       const textResponse = await response.text();
//       console.log("Delete Response:", textResponse);

//       if (
//         textResponse.toLowerCase().includes("deleted") ||
//         textResponse.toLowerCase().includes("success")
//       ) {
//         setResponseData((prevData) =>
//           prevData.filter((item) => item.CompID !== deleteId)
//         );
//         setFilteredData((prevData) =>
//           prevData.filter((item) => item.CompID !== deleteId)
//         );
//       } else {
//         throw new Error(`API responded with failure: ${textResponse}`);
//       }
//     } catch (error) {
//       console.error("Delete Error:", error);
//     }
//   };

//   // Pagination calculations
//   const lastIndex = currentPage * recordsPerPage;
//   const firstIndex = lastIndex - recordsPerPage;
//   const currentRecords = filteredData.slice(firstIndex, lastIndex);
//   const totalPages = Math.ceil(filteredData.length / recordsPerPage);

//   // Get logo URL or placeholder if not available
//   const getLogoUrl = (fileName) => {
//     if (!fileName || fileName === "null") return null;
//     const cleanedPath = fileName.replace(/^~\//, "");
//     return `https://etour.responseinfoway.com/${cleanedPath}`;
//   };

//   return (
//     <div className="masdata_container">
//       {loading && <p className="loading-message">Loading data...</p>}
//       {error && <p className="error-message">{error}</p>}

//       <div className="Payment_div">
//         <div className="payment_maintext">
//           <h6>Company</h6>

//           <div>
//             <Link className="payment_breadcrumbs" to={"/selectdata"}>
//               <p>Master</p>
//             </Link>
//             <span>
//               <i className="fa-solid fa-chevron-right"></i>Company
//             </span>
//           </div>
//         </div>
//         <div className="button_search-payment">
//           <Link to={"/compney"}>
//             <button type="button" className="btn btn">
//               <i className="fa-solid fa-plus"></i> Add Company
//             </button>
//           </Link>
//           <div className="search-input_box">
//             <input
//               type="search"
//               className="form-control"
//               placeholder="Search for name or designation..."
//               onChange={(e) => setSearchQuery(e.target.value)}
//             />
//           </div>
//         </div>
//       </div>

//       <div className="search-container mt-3">
//         <div className="records-per-page-container">
//           <label htmlFor="recordsPerPage">Records Per Page :</label>
//           <select
//             id="recordsPerPage"
//             value={recordsPerPage}
//             onChange={(e) => setRecordsPerPage(Number(e.target.value))}
//           >
//             <option value={5}>5</option>
//             <option value={10}>10</option>
//             <option value={15}>15</option>
//             <option value={20}>20</option>
//           </select>
//         </div>
//       </div>

//       {filteredData.length > 0 ? (
//         <div className="response-container">
//           <div className="table-wrapper">
//             <table className="data-table">
//               <thead>
//                 <tr>
//                   {Object.keys(filteredData[0])
//                     .filter((key) => !hiddenColumns.includes(key))
//                     .map((key) => (
//                       <th key={key}>
//                         {key.charAt(0).toUpperCase() + key.slice(1)}
//                       </th>
//                     ))}
//                   <th>Actions</th>
//                 </tr>
//               </thead>

//               <tbody>
//                 {currentRecords.map((item, index) => (
//                   <tr key={index}>
//                     {Object.entries(item)
//                       .filter(([key]) => !hiddenColumns.includes(key))
//                       .map(([key, val], i) => (
//                         <td
//                           key={i}
//                           className={key === "Name" ? "name-column" : ""}
//                           style={{
//                             ...(key === "Address" && { width: "200px" }),
//                             ...(key === "Name" && {
//                               Width: "150px",
//                             }),
//                           }}
//                         >
//                           {key === "LogoPath" ? (
//                             getLogoUrl(item.LogoPath) ? (
//                               <img
//                                 src={getLogoUrl(item.LogoPath)}
//                                 alt="Logo"
//                                 style={{
//                                   width: "60px",
//                                   height: "60px",
//                                   objectFit: "contain",
//                                 }}
//                               />
//                             ) : (
//                               <img
//                                 src="https://via.placeholder.com/60x60?text=No+Logo"
//                                 alt="No Logo"
//                                 style={{
//                                   width: "60px",
//                                   height: "60px",
//                                   objectFit: "contain",
//                                 }}
//                               />
//                             )
//                           ) : key === "StateID" ? (
//                             getStateName(val)
//                           ) : (
//                             val?.toString() || "N/A"
//                           )}
//                         </td>
//                       ))}

//                     <td>
//                       <div className="seletdata_edit-delet-btn">
//                         <Link
//                           to="/compupdatedata"
//                           state={{ responseData: item }}
//                         >
//                           <button className="selet_edit-btn">
//                             <i className="fa-solid fa-pen-to-square"></i>
//                           </button>
//                         </Link>

//                         <button
//                           className="select_delete-btn"
//                           data-bs-toggle="modal"
//                           data-bs-target="#deleteConfirmationModal"
//                           onClick={() => confirmDelete(item.CompID)}
//                         >
//                           <i className="fa-solid fa-trash"></i>
//                         </button>
//                       </div>
//                     </td>
//                   </tr>
//                 ))}
//               </tbody>
//             </table>
//           </div>

//           <div className="select_pagination mt-4">
//             <button
//               onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
//               disabled={currentPage === 1}
//             >
//               Previous
//             </button>
//             <span>
//               {currentPage} of {totalPages}
//             </span>
//             <button
//               onClick={() =>
//                 setCurrentPage((prev) => Math.min(prev + 1, totalPages))
//               }
//               disabled={currentPage === totalPages}
//             >
//               Next
//             </button>
//           </div>
//         </div>
//       ) : (
//         !loading && <p className="no-data-message">No records found.</p>
//       )}

//       {/* Delete Confirmation Modal */}
//       <div className="modal fade" id="deleteConfirmationModal">
//         <div className="modal-dialog modal-dialog-centered">
//           <div className="modal-content">
//             <div className="close_topbutton-taxes">
//               <button
//                 type="button"
//                 className="btn-close"
//                 data-bs-dismiss="modal"
//               ></button>
//             </div>
//             <div className="delet_icon-boxtaxes">
//               <i className="fa-solid fa-trash"></i>
//             </div>
//             <div className="taxes_pcolom">
//               <h6>Confirm Delete</h6>
//               <p>Are you sure you want to delete?</p>
//             </div>
//             <div className="close_bottombutton-taxes">
//               <button
//                 type="button"
//                 className="btn btn-secondary"
//                 data-bs-dismiss="modal"
//               >
//                 Cancel
//               </button>

//               <button
//                 type="button"
//                 className="btn btn-danger"
//                 data-bs-dismiss="modal"
//                 onClick={handleDelete}
//               >
//                 Delete
//               </button>
//             </div>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default Selectcompny;
